import {
	ActionItem,
	ExecuteItem, HelpItem,
	KNOWN_GROUPS,
	Menu,
	MenuGroup,
	MenuItem,
	type NavigateItem, NEW_RECS_GROUP_ID,
	OnClickHandler, OptionItem,
	SeparatorItem,
	SideMenu,
	TopMenu
} from "@t/ui/Menu.ts";
import {FormEnvironment} from "@fd/Types.ts";
import {
	ACTIVITY_REFS,
	addContextParams,
	AppContextMap,
	BUSCASE_REFS,
	FACT_REFS,
	OFFER_REFS,
	OPPORTUNITY_REFS,
	PROBLEM_REFS,
	toQueryString
} from "@t/fd/AppContext.ts";
import {openNewMailWindow} from "@/intouch";
import {ContactType} from "@t/contact/Contact.ts";
import startWorkSheetAction from "@/action/StartWorkSheetAction.ts";
import {RowObjectRef} from "@t/meta/SourceType.ts";
import {OfferConcept} from "@t/offer/Offer.ts";
import {BusCaseType} from "@t/buscase/BusCase.ts";
import {ListItemBase} from "@t/list/List.ts";
import {ComputedRef, Ref} from "vue";

export type MenuGroupRef = string | MenuGroup;
export type MenuModFunc = (mb:MenuBuilder) => void;
export type SubMenuModFunc = (mb:SubMenuBuilder) => void;
export type MenuGroupModFunc = (gb:GroupBuilder) => void;


export interface ListItemWitIcon extends ListItemBase {
	icon: string;
}


export interface PreloadedMenuItems {
	offerConcepts: Ref<OfferConcept[]>;
	busCaseTypes: Ref<BusCaseType[]>;
	problemTypes: Ref<ListItemWitIcon[]>;
}

export abstract class ItemsBuilder {
	public readonly env:FormEnvironment;
	public readonly ctx?: AppContextMap;
	public readonly oref?: RowObjectRef;
	readonly preloads:PreloadedMenuItems;


	constructor(env: FormEnvironment, preloads:PreloadedMenuItems, oref?:RowObjectRef, ctx?: AppContextMap) {
		this.env = env;
		this.ctx = ctx;
		this.oref = oref;
		this.preloads = preloads;
	}

	navItem(id:string, url:string, titleRes:string,
	        icon:string, addCtx:boolean|string[] = false,
	        target:string = null, confirm:string = null):NavigateItem {
		if (addCtx) url = addContextParams(url, this.ctx, addCtx);
		return this.addItem(<NavigateItem>{
			name: id,
			type: "nav",
			icon: icon,
			title: this.env.msg(titleRes),
			link: url,
			target: target,
			confirm: confirm ? this.env.msg(confirm) : null
		});
	}

	optionItem(id:string, titleRes:string, icon?: string) {
		return this.addItem(<OptionItem>{
			name: id,
			type: "option",
			icon: icon,
			title: this.env.msg(titleRes)
		});
	}

	helpItem(id:string, textRes:string, icon?: string) {
		return this.addItem(<HelpItem>{
			name: id,
			type: "help",
			icon: icon,
			title: this.env.msg(textRes)
		});
	}

	executeItem(id:string, onclick:string|OnClickHandler, titleRes:string, icon:string):ExecuteItem {
		return this.addItem(<ExecuteItem>{
			name: id,
			type: "action",
			icon: icon,
			title: this.env.msg(titleRes),
			onclick: onclick
		});
	}

	separator(uid:string):SeparatorItem {
		return this.addItem({ name: uid, type: "separator" });
	}

	confirmDelete<T extends ActionItem>(menuItem:T, msgKey: string):T {
		menuItem.confirm = this.env.msg(msgKey);
		menuItem.confirmYes = this.env.msg('butt.delete');
		menuItem.confirmNo = this.env.msg('butt.cancel');
		menuItem.confirmIcon = "trash";
		menuItem.confirmIconColor = "#EE3300";
		return menuItem;
	}

	abstract addItem<T extends MenuItem>(item:T):T;

	add(item:MenuItem):ItemsBuilder {
		this.addItem(item);
		return this;
	}
}


export class SubMenuBuilder extends ItemsBuilder {
	public readonly menu:Menu;

	constructor(env: FormEnvironment, preloads:PreloadedMenuItems, m: Menu, oref?:RowObjectRef, ctx?: AppContextMap) {
		super(env, preloads, oref, ctx);
		this.menu = m;
	}

	addItem<T extends MenuItem>(item:T):T {
		if (!Array.isArray(this.menu?.children)) this.menu.children = [];
		this.menu.children.push(item);
		return item;
	}
}


export class GroupBuilder extends ItemsBuilder {
	public readonly group:MenuGroup;

	constructor(env: FormEnvironment, preloads:PreloadedMenuItems, g: MenuGroup, oref?:RowObjectRef, ctx?: AppContextMap) {
		super(env, preloads, oref, ctx);
		this.group = g;
	}

	addItem<T extends MenuItem>(item:T):T {
		if (!Array.isArray(this.group?.items)) this.group.items = [];
		this.group.items.push(item);
		return item;
	}

	inMenu(id:string, titleRes:string, icon:string, fn:SubMenuModFunc):SubMenuBuilder {
		const m:Menu = this.addItem({ name: id, type: "menu", icon:icon, title: this.env.msg(titleRes), children:[] });
		let smb = new SubMenuBuilder(this.env, this.preloads, m, this.oref, this.ctx);
		if (fn != null) fn(smb);
		return smb;
	}
}

/** Interface for components willing to add menu items to the sidebar */
export class MenuBuilder {
	private readonly env:FormEnvironment;
	public readonly menu:SideMenu;
	readonly preloads:PreloadedMenuItems;
	public readonly ctx?: AppContextMap;
	public readonly oref?: RowObjectRef;
	public pageNames: Set<string>;

	constructor(env:FormEnvironment, preloads:PreloadedMenuItems, m:SideMenu, oref?:RowObjectRef, ctx?: AppContextMap) {
		this.menu = m;
		this.env = env;
		this.preloads = preloads;
		this.oref = oref;
		this.ctx = ctx;
		this.pageNames = new Set<string>();
	}

	private sortInGroup(g:MenuGroup):MenuGroup {
		for (let i = 0; i < this.menu.groups.length; i++) {
			const pr = this.menu.groups[i].priority ?? 0;
			if (pr > g.priority) {
				this.menu.groups.splice(i, 0, g);
				return g;
			}
		}
		this.menu.groups.push(g);
		return g;
	}

	private cloneTemplateGroup(mg:MenuGroup):MenuGroup {
		const g = structuredClone(mg);
		if (g.title) g.title = this.env.msg(g.title);
		return g;
	}

	private ensureGroup(group:MenuGroupRef):MenuGroup {
		if (typeof group === 'string') {
			for (let existingGroup of this.menu.groups) {
				if (existingGroup.name === group) return existingGroup;
			}
			// We don't have this group
			for (let kg of KNOWN_GROUPS) {
				if (kg.name === group) {
					// This is known group id
					return this.sortInGroup(this.cloneTemplateGroup(kg))
				}
			}
			throw new Error("Unknown menu group ID: " + group);
		} else {
			for (let existingGroup of this.menu.groups) {
				if (existingGroup.name === group.name) return existingGroup;
			}
			return this.sortInGroup(group);
		}
	}

	inGroup(group:MenuGroupRef, fn:MenuGroupModFunc = null):GroupBuilder {
		const g = this.ensureGroup(group);
		let gb = new GroupBuilder(this.env, this.preloads, g, this.oref, this.ctx);
		if (fn != null) fn(gb);
		return gb;
	}

	setSideMenu(sm:SideMenu):void {
		this.menu.groups.push(...sm.groups);
	}

	standardMenu():void {
		this.inGroup(NEW_RECS_GROUP_ID, (gb) => {
			gb.navItem("createOrg", "createParty.do?type=ORGANIZATION", "menu.createOrg", "org");
			gb.navItem("createPerson", "createParty.do?type=INDIVIDUAL", "menu.createPerson", "individual");
		});
	}

	pageNamed(page:string):MenuBuilder {
		this.pageNames.add(page);
		return this;
	}

	getPageNames():Set<string> {
		return this.pageNames;
	}
}



export function standardNewRecordGroup(options = {
	mailMembers: false,
	facts: false,
	party: true
}):MenuGroupModFunc {
	return (gb:GroupBuilder) => {
		if (Server.mod.communication) gb.navItem("createContact", "createContact.do", "menu.createContact", "call", ACTIVITY_REFS);
		gb.navItem("planEvent", "planEvent.do", "menu.planEvent", "event", ACTIVITY_REFS);
		gb.navItem("createTask", "createTask.do", "menu.createTask", "task", ACTIVITY_REFS);
		if (Server.mod.email && Server.hasMail) {
			gb.executeItem("newMail", () => { openNewMailWindow(toQueryString(gb.ctx, ACTIVITY_REFS)) }, "menu.composeMail", "send");
			if (options.mailMembers && gb.oref?.id) gb.executeItem("newMailMembers", () => { openNewMailWindow(toQueryString(gb.ctx, ACTIVITY_REFS)+"&includeMembers=true&root="+gb.oref.id) }, "menu.composeMailMembers", "bulk-email");
		}
		if (Server.mod.sms) gb.navItem("newSMS", "createSMS.do", "menu.createSMS", "mobile-messages", ACTIVITY_REFS);
		if (Server.mod.communication) gb.navItem("createLetter", "createContact.do?type="+ContactType.LETTER, "menu.composeLetter", "mail", ACTIVITY_REFS);
		gb.separator("sep-new-1");
		if (Server.mod.opportunity) {
			gb.navItem("createOpportunity", "createOpportunity.do", "menu.createOpportunity", "opportunity", OPPORTUNITY_REFS);
		}
		if (Server.mod.offer) {
			gb.inMenu("createOffer", "menu.createOffer", "offer", (mb) => {
				mb.navItem("createOffer0", "createOffer.do", "offer.noTemplate", "offer", OFFER_REFS);
				for (let ot of gb.preloads.offerConcepts.value) {
					mb.navItem("createOffer"+ot.id, "createOffer.do?offer="+ot.id, ot.name, "offer-template", OFFER_REFS);
				}
			});
		}
		if (Server.mod.buscase) {
			gb.inMenu("createBusCase", "menu.createBusCase", "case", (mb) => {
				for (let bct of gb.preloads.busCaseTypes.value) {
					mb.navItem("createBusCase"+bct.id, "createBusCase.do?bctype="+bct.id, bct.localizedName, bct.icon, BUSCASE_REFS);
				}
			});
		}
		if (Server.mod.helpdesk) {
			gb.inMenu("createProblem", "menu.createProblem", "flash", (mb) => {
				mb.navItem("createProblem", "createProblem.do?problemType=6req", "menu.createProblem", "flash", PROBLEM_REFS);
				for (let pt of gb.preloads.problemTypes.value) {
					mb.navItem("createProblem_"+pt.value, "createProblem.do?problemType="+pt.value, pt.name, pt.icon, PROBLEM_REFS);
				}
			});
		}
		if (options.party) {
			gb.separator("sep-new-2");
			gb.navItem("createOrg", "createParty.do?type=ORGANIZATION", "menu.createOrg", "org");
			gb.navItem("createPerson", "createParty.do?type=INDIVIDUAL", "menu.createPerson", "individual");
		}
		gb.separator("sep-new-3");
		if (Server.mod.worksheet && gb.oref?.id) {
			gb.navItem("createWorkSheet", "createWorkSheet.do?srcType="+gb.oref.type+"&srcId="+gb.oref.id, "menu.createWorkSheet", "worksheet");
			gb.executeItem("startWorkSheet", (env) => { startWorkSheetAction(env, gb.oref.type, gb.oref.id) }, "menu.startWorkSheet", "timer");
		}
		if (options.facts) gb.navItem("createFact", "createFact.do", "menu.createFact", "fact", FACT_REFS);
	};
}
